import type { ReportNameType } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { DateTime } from 'luxon';
import type { ChartType, TimestepType } from './types';

export const OverTimeOptions = [
  { label: 'Total', value: 'total', id: 'total_total' },
  { label: 'Over time', value: 'over_time', id: 'total_over_time' },
];

const oldGroupByAccount = { label: 'By account', value: 'ACCOUNT', id: 'breakdown_by_account' };
const oldGroupByCategory = { label: 'By category', value: 'CATEGORY', id: 'breakdown_by_category' };
const oldGroupByPayee = { label: 'By payee', value: 'PAYEE', id: 'breakdown_by_payee' };
const oldGroupByTag = { label: 'By tag', value: 'TAG', id: 'breakdown_by_tag' };
const oldGroupByNone = { label: 'By none', value: 'NONE', id: 'breakdown_by_none' };

const categoryOptions = [oldGroupByCategory, oldGroupByPayee, oldGroupByTag, oldGroupByNone];
const savingsOptions = [oldGroupByAccount, oldGroupByNone];
const customGroupByOptions = [oldGroupByAccount, oldGroupByCategory, oldGroupByPayee, oldGroupByTag, oldGroupByNone];

export function getFilterOptionsFromTabName(tabName: ReportNameType | 'overview') {
  switch (tabName) {
    case 'overview':
    case 'total-spending':
    case 'income':
      return categoryOptions;
    case 'savings':
      return savingsOptions;
    case 'custom':
      return customGroupByOptions;
    case 'net-worth':
    case 'net-income':
    case 'taxes':
    case 'balance-sheet':
    default:
      return null;
  }
}

export function findGroupByCategoryId(
  catId: string,
  // @ts-expect-error FIXME TS7006
  group,
  crumbs: { name: string; categoryId?: string; total?: string }[] = [],
) {
  if (!group) return [null, crumbs];
  if (catId === 'EverythingElse' && group.groupKey === 'Everything else') {
    return [group, crumbs.concat([{ name: group.groupKey, categoryId: group.categoryId, total: group.total }])];
  }

  if (group.categoryId === catId) return [group, crumbs];

  let groupFound = null;
  let crumbsFound = crumbs;
  // @ts-expect-error FIXME TS7006
  group.subgroups?.some((subG) => {
    [groupFound, crumbsFound] = findGroupByCategoryId(
      catId,
      subG,
      crumbs.concat([{ name: subG.groupKey, categoryId: subG.categoryId, total: subG.total }]),
    );
    return groupFound;
  });

  return [groupFound, crumbsFound];
}

/**
 * Formats timestep for display
 */
export const getTimestepFormatting = (timestep: string) => {
  let categoryViewTimestep = 'LLLL yyyy';
  let tooltipTimestep = 'LLLL yyyy';

  switch (timestep) {
    case 'DAY':
      categoryViewTimestep = 'd';
      tooltipTimestep = 'LLLL d, yyyy';
      break;
    case 'YEAR':
      categoryViewTimestep = 'yyyy';
      tooltipTimestep = 'yyyy';
      break;
    default:
      break;
  }
  return { categoryViewTimestep, tooltipTimestep };
};

const DAILY_BAR_CHART_TYPES = new Set<ChartType>(['BAR_GROUPED', 'BAR_STACKED']);

export const formatTransactionChartCategory = (
  timeStamp: string,
  timestep: TimestepType,
  chartType: ChartType,
): string => {
  const date = DateTime.fromISO(timeStamp);

  if (timestep === 'DAY' && DAILY_BAR_CHART_TYPES.has(chartType)) {
    return date.toFormat('MMM d');
  }

  if (chartType === 'MULTILINE') {
    return date.toFormat('yyyy-MM-dd');
  }

  return date.toFormat(getTimestepFormatting(timestep).categoryViewTimestep);
};

/**
 * Transforms chart data with date formatting
 */
export const transformChartData = (periods: any[], timestep: string, chartType: string, findRelevantPeriod: number) => {
  return periods.map((per, idx) => ({
    ...per,
    total: per.total === 0 && idx < findRelevantPeriod ? null : per.total,
    txns: undefined, // Remove txns for optimization
    category: chartType === 'MULTILINE' ? per.timeStamp : per.timeStamp, // DateTime formatting would be applied here
    custom: per.timeStamp, // DateTime formatting would be applied here
  }));
};

/**
 * Determines if we should show over-time chart
 */
export const shouldShowOverTime = (timestep: string): boolean => {
  return timestep !== 'NONE';
};

/**
 * Gets chart container styles based on chart type
 */
export const getChartContainerStyles = (chartType: string, theme: any) => {
  return ['PIE', 'BAR_SIDEWAYS'].includes(chartType)
    ? { width: '75%' }
    : {
        [theme.breakpoints.up('md')]: {
          width: '100%',
        },
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      };
};

export const sortSimpleChartData = (a: any, b: any) => {
  const aIsOther = a.category === 'Everything else';
  const bIsOther = b.category === 'Everything else';

  // If a is "Everything else", push it down
  if (aIsOther && !bIsOther) return 1;

  // If b is "Everything else", push it down
  if (bIsOther && !aIsOther) return -1;

  // Otherwise sort by total descending
  return (b.total || 0) - (a.total || 0);
};
